import { useEffect } from 'react';

import { useLocation } from 'react-router';

import { useNotification } from 'hooks/useNotification';
import NotificationWrapper from 'primitives/NotificationWrapper/NotificationWrapper';
import { Stack } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';

import { breakpoints } from 'styles/theme.variables';

// High-impact page-level banners: never show more than 3 at once.
export const MAX_BANNERS = 3;

// Frames with zero movement that mean the smooth scroll isn't running: past the
// first-frame warm-up, well inside a real animation.
const STALLED_FRAMES = 3;

// Stable per-render styles for the page banners:
// - drop the theme's desktop-only top margin on success/caution so the Stack
//   gap is the only vertical spacing (even gaps across variants)
// - allow multi-line wrapping (the shared theme otherwise clamps
//   success/caution to a single ellipsised line)
const PAGE_BANNER_STYLES = {
  root: { marginTop: 0 },
  description: {
    height: 'auto',
    overflow: 'visible',
    textOverflow: 'clip',
    whiteSpace: 'normal',
  },
};

export default function ProtectedNotificationBanners() {
  const { notificationBanners, setNotificationBanners, bannerSignal } = useNotification();
  const location = useLocation();
  const isMobile = useMediaQuery(`(max-width: ${breakpoints.maxMobile})`);

  // if the user changes pages, clear the notifications
  useEffect(() => {
    setNotificationBanners([]);
  }, [location.pathname]);

  // Scroll to the top whenever a banner is added, so it isn't missed if the user is
  // scrolled down. Keyed off bannerSignal, not the banner count: re-triggering the same
  // notification keeps the stack the same length but still has to scroll.
  useEffect(() => {
    if (!bannerSignal) return undefined;

    let frame;
    let lastY = null;
    let stalledFrames = 0;

    // A smooth scroll can be cancelled before it ever moves - a closing overlay's reflow
    // and route content mounting both do it. So watch it frame by frame: if nothing has
    // moved for STALLED_FRAMES, no animation is running, and an instant scroll finishes
    // the job (that one can't be cancelled).
    const watch = () => {
      const y = window.scrollY;
      if (y === 0) return; // landed
      if (lastY !== null && y > lastY) return; // user scrolled away - leave them alone
      stalledFrames = y === lastY ? stalledFrames + 1 : 0;
      lastY = y;
      if (stalledFrames >= STALLED_FRAMES) {
        window.scrollTo({ top: 0, behavior: 'auto' });
        return;
      }
      frame = requestAnimationFrame(watch);
    };

    const scrollToTop = () => {
      if (window.scrollY === 0) return;
      window.scrollTo({
        top: 0,
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches
          ? 'auto'
          : 'smooth',
      });
      frame = requestAnimationFrame(watch);
    };

    // A banner added as a Drawer/Modal closes (e.g. Cuisine Categories) lands while the
    // body is still scroll-locked (body[data-scroll-locked] sets overflow: hidden), where
    // scrollTo is a no-op. Mantine (react-remove-scroll) drops that attribute in an effect
    // cleanup, and the observer fires as a microtask inside that same commit - so always
    // hand off to the next frame, letting the style change and any reflow land first.
    if (!document.body.hasAttribute('data-scroll-locked')) {
      frame = requestAnimationFrame(scrollToTop);
      return () => cancelAnimationFrame(frame);
    }
    const observer = new MutationObserver(() => {
      if (!document.body.hasAttribute('data-scroll-locked')) {
        observer.disconnect();
        frame = requestAnimationFrame(scrollToTop);
      }
    });
    observer.observe(document.body, { attributes: true, attributeFilter: ['data-scroll-locked'] });
    return () => {
      observer.disconnect();
      cancelAnimationFrame(frame);
    };
  }, [bannerSignal]);

  const removeNotification = index => {
    const updatedNotifications = [...notificationBanners];
    updatedNotifications.splice(index, 1);
    setNotificationBanners(updatedNotifications);
  };

  if (!notificationBanners?.length) {
    return null;
  }

  return (
    <Stack gap={isMobile ? 'sm' : 'md'} mt={isMobile ? 'sm' : 'md'}>
      {notificationBanners.slice(0, MAX_BANNERS).map((banner, index) => {
        const isDismissible = banner.dismissible ?? true;
        // No forced label: with an indicator and no label, every variant
        // (incl. neutral/blue) renders a dot. The labeled chip is opt-in via
        // an explicit `label` (e.g. 'Info').
        const { label } = banner;
        return (
          <NotificationWrapper
            // eslint-disable-next-line react/no-array-index-key
            key={`banner_${index}`}
            indicator={banner.indicator ?? true}
            label={label}
            variant={banner.variant}
            index={index}
            onClose={isDismissible ? removeNotification : undefined}
            size={banner.size ?? 'compact'}
            // a11y: assertive announcement for caution/errors, polite otherwise;
            // give the close button an accessible name.
            role={banner.variant === 'caution' ? 'alert' : 'status'}
            closeButtonLabel="Dismiss notification"
            styles={PAGE_BANNER_STYLES}
          >
            {banner.text}
          </NotificationWrapper>
        );
      })}
    </Stack>
  );
}
